import { router } from '@/router';
import { updateToken } from '@/store/authSlice';
import { useEffect, useState } from 'react';
import cache from 'lscache';
import { useAppDispatch, useAppSelector } from '@/store/store';
import { initUser } from '@/store/userSlice';

import { getUserRole } from './auth.utils';
import { userManager } from './UserManager';
import { TOKEN_KEY } from './auth.constants';
import { USER_LANGUAGE_STORAGE_KEY } from '../LanguagePicker/language-picker.constants';

export const useAuthListeners = () => {
  const dispatch = useAppDispatch();
  const [isOidcReady, setIsOidcReady] = useState(false);
  const idpUrl = useAppSelector(state => state.auth.idpUrl);
  const clientId = useAppSelector(state => state.auth.clientId);

  useEffect(() => {
    if (!isOidcReady && idpUrl && clientId) {
      if (!userManager.getUser()) {
        userManager.init({ idpUrl, clientId });
      }

      const client = userManager.getUser();

      client.events.addUserLoaded(loadedUser => {
        if (!loadedUser.id_token) return;

        cache.set(TOKEN_KEY, loadedUser.access_token, loadedUser.expires_in ?? 3600 / 60);

        const userLang = cache.get(USER_LANGUAGE_STORAGE_KEY);
        const preferredLanguage = userLang || loadedUser.profile.preferredLanguage || 'EN';
        const role = getUserRole(loadedUser.profile.role as string[]);

        dispatch(initUser({ displayName: loadedUser.profile.displayName as string, preferredLanguage, role }));
        dispatch(updateToken(loadedUser.access_token));
      });

      client.events.addSilentRenewError(() => {
        cache.remove(TOKEN_KEY);
        router.invalidate().catch(() => {});
      });

      const initAuthUser = async () => {
        try {
          const user = await client.getUser();

          if (user && !user.expired) {
            const userLang = cache.get(USER_LANGUAGE_STORAGE_KEY);
            const preferredLanguage = userLang || user.profile.preferredLanguage || 'EN';
            const role = getUserRole(user.profile.role as string[]);

            dispatch(initUser({ displayName: user.profile.displayName as string, preferredLanguage, role }));
            dispatch(updateToken(user.access_token));
          } else {
            cache.remove(TOKEN_KEY);
            await router.invalidate();
          }
        } catch {
          cache.remove(TOKEN_KEY);
          router.invalidate().catch(() => {});
        } finally {
          setIsOidcReady(true);
        }
      };

      initAuthUser().catch(() => {});
    }
  }, [idpUrl, clientId, isOidcReady, dispatch]);

  return { isOidcReady };
};
